import * as d3 from "d3";
import { queue } from "d3-queue";
import { geoBounds, geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson";
import { numberWithCommas } from "../utils";

export const drawMap = (
    mapEl,
    locationName,
    regionData,
    handleContextMenuClick,
    handleDrilldownClick,
    regionColors,
    downloading,
    handleCountryChange,
    countries,
    custom_chart_id,
    previousScale,
    handleZoomChange,
    subregionData,
    showSubRegion,
    dimensions,
    isPublic,
    showChartsDrilldown,
) => {
    if (!custom_chart_id) {
        custom_chart_id = "";
    }

    let config = {
        country: locationName.toLowerCase().replace(" ", ""),
    };
    var currentsubunits = "country";
    var stateName = "";

    const getDataForState = (d) => {
        if (d.properties.NAME_1) {
            if (currentsubunits === "country") return regionData.find((loc) => loc.name === d.properties.NAME_1.toLowerCase());
            else return regionData.find((loc) => loc.name === d.properties.NAME_1.toLowerCase() && loc.country === d.properties.country.toLowerCase());
        }
    };
    const getDataForSubRegions = (d) => {
        if (d.properties.NAME_2) {
            if (currentsubunits === "country") return subregionData.find((loc) => loc.name == d.properties.NAME_2);
            return subregionData.find((loc) => loc.name == d.properties.NAME_2.toLowerCase() && loc.country === d.properties.country);
        }
    };
    var width, height, center;

    if (dimensions.width) {
        width = dimensions.width;
        height = dimensions.height;
        center = [width / 2, height / 2];
    } else {
        if (locationName == "World") {
            width = d3.select(mapEl).node().getBoundingClientRect().width - 10;
            height = d3.select(mapEl).node().getBoundingClientRect().height - 20;
            center = [width / 2, height / 2];
        } else {
            width = mapEl.getBoundingClientRect().width;
            height = mapEl.getBoundingClientRect().height;
        }
    }

    let geoFile = `${process.env.PUBLIC_URL}/mapsjson/${locationName.toLowerCase()}.topojson`;
    let geoFileWorldStates = `${process.env.PUBLIC_URL}/mapsjson/worldstates.topojson`;
    var totalDataRange = 0;
    d3.select(mapEl).html(null);

    // main container
    var svg;
    if (locationName == "World" || showSubRegion) {
        svg = d3
            .select(mapEl)
            .append("svg")
            .attr("width", width + "px")
            .attr("height", height + "px")
            .style("position", "relative")
            .append("g")
            .attr("class", "mapg" + custom_chart_id);
        //var g = svg.append("g");
    } else {
        svg = d3
            .select(mapEl)
            .append("svg")
            .attr("width", width + "px")
            .attr("height", height + "px")
            .style("position", "relative");
    }

    const colors = (val) => {
        for (let i in regionColors) {
            if (val >= regionColors[i].from && val <= regionColors[i].to) {
                return regionColors[i].color;
            }
        }
    };

    if (locationName == "World") {
        queue()
            .defer(d3.json, geoFile)
            .defer(d3.json, geoFileWorldStates)
            .await((error, data1, data2) => {
                onDataLoad(error, data1, data2);
            });
    } else {
        d3.json(geoFile, (error, data) => {
            onDataLoad(error, data);
        });
    }

    const onDataLoad = (err, data, worldstates) => {
        if (err) {
            console.error("couldn't fetch map topojson data: " + geoFile + "\n " + err);
            return false;
        }

        var subunits = feature(data, data.objects[config.country]);
        var subunitszoomed;
        if (locationName == "World") {
            subunitszoomed = feature(worldstates, worldstates.objects["world"]);
        }
        var bounds = geoBounds(subunits);

        var projection;
        if (locationName == "World") {
            projection = geoMercator()
                .scale(80)
                .translate([width / 2, height / 2]);
        } else {
            var padding = 10;
            projection = geoMercator().fitExtent(
                [
                    [padding, padding],
                    [width - padding * 2, height - padding * 2],
                ],
                subunits,
            );
        }

        var path = geoPath().projection(projection);

        let subunitElem = svg.selectAll(".subunit").data(subunits.features);

        const showContextMenu = (d, countryObject) => {
            if (isPublic) return;
            let contextMenu = d3.select(".dashboard-charts-contextmenu");
            let e = d3.event;
            e.preventDefault();
            contextMenu.style("top", `${e.clientY}px`);
            contextMenu.style("left", `${e.clientX}px`);
            contextMenu.html(null);
            contextMenu
                .append("li")
                .text("View Conversations")
                .on("click", () => handleContextMenuClick(d, countryObject));
            contextMenu
                .append("li")
                .text("View Insights")
                .on("click", () => {
                    handleDrilldownClick(d, countryObject);
                    showChartsDrilldown(true, "mentionsMap");
                });
            contextMenu.style("display", "block");
        };

        const hashCode = (s) => {
            return s.split("").reduce(function (a, b) {
                a = (a << 5) - a + b.charCodeAt(0);
                return a & a;
            }, 0);
        };

        const clicked = (d) => {
            let object;
            if (currentsubunits === "country") {
                if (showSubRegion && d.properties.NAME_2) object = countries.find((elem) => elem.name.toLowerCase() === d.properties.NAME_2);
                else object = countries.find((elem) => elem.name && elem.name.toLowerCase() === d.properties.NAME_1.toLowerCase());
            } else object = countries.find((elem) => elem.name && elem.name.toLowerCase() === d.properties.country.toLowerCase());
            if (object) handleCountryChange(object.name);
        };

        const zoomed = (d) => {
            d3.select(".mapg" + custom_chart_id).attr("transform", "translate(" + d3.event.translate + ")scale(" + d3.event.scale + ")");
            handleZoomChange([d3.event.translate, d3.event.scale]);
            onZoom();
        };

        const onZoom = (scale) => {
            scale = scale === undefined ? d3.event.scale : scale;
            var subunitvalue;
            if (scale > 3 && currentsubunits === "country") {
                currentsubunits = "state";
                subunitvalue = { ...subunitszoomed };
            } else if (scale <= 3 && currentsubunits === "state") {
                currentsubunits = "country";
                subunitvalue = { ...subunits };
            }
            if (subunitvalue === undefined) return;
            svg.selectAll(".subunit").remove();
            subunitElem = svg.selectAll(".subunit").data(subunitvalue.features);
            renderChart();
        };

        d3.select("#zoom-in" + custom_chart_id).on("click", function () {
            var scale = zoom.scale(),
                extent = zoom.scaleExtent(),
                translate = zoom.translate();
            var x = translate[0],
                y = translate[1];
            var factor = 1.2;

            var target_scale = scale * factor;

            if (scale === extent[1]) {
                return false;
            }
            var clamped_target_scale = Math.max(extent[0], Math.min(extent[1], target_scale));
            if (clamped_target_scale != target_scale) {
                target_scale = clamped_target_scale;
                factor = target_scale / scale;
            }
            x = (x - center[0]) * factor + center[0];
            y = (y - center[1]) * factor + center[1];

            zoom.scale(target_scale).translate([x, y]);

            d3.select(".mapg" + custom_chart_id)
                .transition()
                .attr("transform", "translate(" + zoom.translate().join(",") + ") scale(" + zoom.scale() + ")");
            d3.select(".mapg" + custom_chart_id)
                .selectAll("path")
                .attr("d", path.projection(projection));
            onZoom(target_scale);
            handleZoomChange([zoom.translate(), target_scale]);
        });

        d3.select("#zoom-out" + custom_chart_id).on("click", function () {
            var scale = zoom.scale(),
                extent = zoom.scaleExtent(),
                translate = zoom.translate();
            var x = translate[0],
                y = translate[1];
            var factor = 1 / 1.2;

            var target_scale = scale * factor;

            if (scale === extent[0]) {
                return false;
            }
            var clamped_target_scale = Math.max(extent[0], Math.min(extent[1], target_scale));
            if (clamped_target_scale != target_scale) {
                target_scale = clamped_target_scale;
                factor = target_scale / scale;
            }
            x = (x - center[0]) * factor + center[0];
            y = (y - center[1]) * factor + center[1];

            zoom.scale(target_scale).translate([x, y]);

            d3.select(".mapg" + custom_chart_id)
                .transition()
                .attr("transform", "translate(" + zoom.translate().join(",") + ") scale(" + zoom.scale() + ")");
            d3.select(".mapg" + custom_chart_id)
                .selectAll("path")
                .attr("d", path.projection(projection));
            onZoom(target_scale);
            handleZoomChange([zoom.translate(), target_scale]);
        });

        d3.select("#reset" + custom_chart_id).on("click", function () {
            zoom.translate([0, 0]);
            zoom.scale(1);
            d3.select(".mapg" + custom_chart_id)
                .transition()
                .attr("transform", "translate(" + zoom.translate().join(",") + ") scale(" + zoom.scale() + ")");
            d3.select(".mapg" + custom_chart_id)
                .selectAll("path")
                .attr("d", path.projection(projection));
            onZoom(1);
            handleZoomChange([zoom.translate(), 1]);
        });

        const scrolled = () => {
            const wrapper = d3.select(".mapg" + custom_chart_id);
            const x = wrapper.node().scrollLeft + wrapper.node().clientWidth;
            const y = wrapper.node().scrollTop + wrapper.node().clientHeight;
            const scale = d3.zoomTransform(wrapper.node()).k;
            // Update zoom parameters based on scrollbar positions.
            wrapper.call(d3.zoom().translateTo, x * scale * scale, y * scale * scale);
        };
        var zoom = d3.behavior.zoom().on("zoom", zoomed);

        // var zoom = d3.zoom().on("zoom", zoomed); // upgrade from v3 to v5
        if (locationName == "World" || showSubRegion) {
            // svg.on("scroll", scrolled).call(zoom);
            svg.on("drag", scrolled).call(zoom);
        }

        let renderChart = () =>
            subunitElem
                .enter()
                .append("path")
                .attr("class", "subunit")
                .attr("d", path)
                .attr("stroke", "white")
                .attr("stroke-width", () => {
                    if (currentsubunits === "country") {
                        return 0.8;
                    } else if (currentsubunits === "state") {
                        return 0.1;
                    }
                })
                .attr("fill", (d) => {
                    let val;
                    if (showSubRegion && d.properties.NAME_2) {
                        val = getDataForSubRegions(d);
                    } else val = getDataForState(d);
                    if (!showSubRegion && d.properties.NAME_1) {
                        val = getDataForState(d);
                    }
                    return val ? colors(val.total) : "#c3c3c3";
                })
                .on("contextmenu", (d) => {
                    if (locationName !== "World" || currentsubunits === "state") {
                        showContextMenu(d);
                    } else {
                        let short_name = d.properties["Alpha_2"].toLowerCase();
                        let object = countries.find((elem) => elem.short_name && elem.short_name.toLowerCase() === short_name);
                        if (object) {
                            showContextMenu(d, object);
                        }
                    }
                })
                .on("click", clicked)
                .append("title")
                .html((d) => {
                    if (showSubRegion && d.properties.NAME_2) {
                        let data = getDataForSubRegions(d);
                        let name2 = d.properties.NAME_2.replaceAll("ā", "a");
                        name2 = name2.replaceAll("Ā", "A");
                        name2 = name2.replaceAll("ī", "i");
                        name2 = name2.replaceAll("Ī", "I");
                        name2 = name2.replaceAll("ē", "e");
                        name2 = name2.replaceAll("Ē", "E");
                        name2 = name2.replaceAll("ō", "o");
                        name2 = name2.replaceAll("Ō", "O");
                        name2 = name2.replaceAll("ū", "u");
                        name2 = name2.replaceAll("Ū", "U");

                        if (data)
                            return `<tspan class="name">${name2
                                .split(" ")
                                .map((n) => n.charAt(0).toUpperCase() + n.substr(1).toLowerCase())
                                .join(" ")}</tspan>\n<tspan class="mentions">Mentions: ${numberWithCommas(
                                data.total,
                            )} </tspan>\n<tspan class="positive">Positive: ${numberWithCommas(
                                data.positive,
                            )} </tspan>\n<tspan class="negative">Negative: ${numberWithCommas(
                                data.negative,
                            )} </tspan>\n<tspan class="neutral">Neutral: ${numberWithCommas(data.neutral)}</tspan>`;
                        else
                            return `<tspan class="name">${name2
                                .split(" ")
                                .map((n) => n.charAt(0).toUpperCase() + n.substr(1).toLowerCase())
                                .join(" ")}</tspan>`;
                    } else {
                        let data = getDataForState(d);
                        let name = d.properties.NAME_1;
                        if (data)
                            return `<tspan class="name">${
                                name &&
                                name
                                    .split(" ")
                                    .map((n) => n.charAt(0).toUpperCase() + n.substr(1).toLowerCase())
                                    .join(" ")
                            }</tspan>\n<tspan class="mentions">Mentions: ${numberWithCommas(
                                data.total,
                            )} </tspan>\n<tspan class="positive">Positive: ${numberWithCommas(
                                data.positive,
                            )} </tspan>\n<tspan class="negative">Negative: ${numberWithCommas(
                                data.negative,
                            )} </tspan>\n<tspan class="neutral">Neutral: ${numberWithCommas(data.neutral)}</tspan>`;
                        else
                            return `<tspan class="name">${
                                name &&
                                name
                                    .split(" ")
                                    .map((n) => n.charAt(0).toUpperCase() + n.substr(1).toLowerCase())
                                    .join(" ")
                            }</tspan>`;
                    }
                });

        renderChart();

        if (previousScale !== null) {
            zoom.scale(previousScale[1]).translate(previousScale[0]);

            d3.select(".mapg" + custom_chart_id)
                .transition()
                .attr("transform", "translate(" + zoom.translate().join(",") + ") scale(" + zoom.scale() + ")");
            d3.select(".mapg" + custom_chart_id)
                .selectAll("path")
                .attr("d", path.projection(projection));
            onZoom(previousScale[1]);
        }
    }; // ---- End of data block ----
};
